/**
 * Booking status helpers — which lifecycle actions are valid for each status,
 * and the non-color-only status variant for the StatusBadge.
 *
 * Confirm/cancel are high-stakes (billing + PMS). The UI only offers actions
 * that are legal for the current status; the backend stays authoritative and a
 * 409 is handled when a state has moved underneath us.
 */

import type { BookingStatus, StatusVariant } from './_deps';

export type BookingAction = 'submit' | 'confirm' | 'cancel';

const ACTIONS: Record<BookingStatus, readonly BookingAction[]> = {
  requested: ['submit', 'cancel'],
  draft: ['submit', 'cancel'],
  pending_staff_approval: ['confirm', 'cancel'],
  confirmed: ['cancel'],
  cancelled: [],
  failed: [],
  expired: [],
};

/** Valid lifecycle actions for the given booking status. */
export function validBookingActions(status: BookingStatus): readonly BookingAction[] {
  return ACTIONS[status];
}

/** Visual (non-color-only) status variant for the StatusBadge. */
export function bookingStatusVariant(status: BookingStatus): StatusVariant {
  switch (status) {
    case 'requested':
    case 'draft':
      return 'neutral';
    case 'pending_staff_approval':
      return 'pending';
    case 'confirmed':
      return 'success';
    case 'cancelled':
      return 'inactive';
    case 'failed':
      return 'error';
    case 'expired':
      return 'warning';
  }
}
